import { Line } from '../mathObjects/Line';
import type { ProblemJSDependencies } from '../problemJSDependencies';
import { BasicObject } from '../uiObjects/basicObject';

import {
  buildModifier,
  Modifier,
  ModifierDTO,
  Parent,
} from './VerticalSolvingModifier';

export interface VerticalSolvingStepDTO {
  // xml encoding of Value
  value: any;
  // array of ModifierDTO
  modifiers: ModifierDTO[];
}

class VerticalSolvingStep extends BasicObject {
  line: Line;
  modifiers: Modifier[];
  problemJS: ProblemJSDependencies;
  dto: VerticalSolvingStepDTO;

  constructor(problemJS: ProblemJSDependencies, dto: VerticalSolvingStepDTO) {
    super();

    this.problemJS = problemJS;
    this.dto = dto;
    this.gmmName = 'verticalSolvingStep';

    const panel = problemJS.getStaticPanel(dto.value);

    if (!(panel instanceof Line)) {
      throw new Error(`VerticalSolvingStep expected Line from getStaticPanel`);
    }

    this.line = panel;

    this.add(this.line);
    this.modifiers = dto.modifiers.map(modifierDTO =>
      buildModifier(this.problemJS, modifierDTO)
    );
    this.modifiers.forEach(modifier => this.add(modifier));

    this.buildSize();
  }

  isBaselineAware(): boolean {
    return true;
  }

  getVertToBaseline(): number {
    return this.line.getVertToBaseline();
  }

  pixelsToMiddleSymbol(): number {
    if (this.line.countMiddleSymbols() !== 1) {
      return 0;
    }

    const pixelsToMiddle = this.line.pixelsToMiddleSymbol();

    if (pixelsToMiddle === 0) {
      return 0;
    }

    // line may have been shifted right (if modifiers had negative x)
    return this.line.viewportX + pixelsToMiddle;
  }

  private buildSize(): void {
    this.line.buildSizeRecursive();

    const idToObject = this.buildIdMap();

    let maxModifierH = 0;

    this.modifiers.forEach(modifier => {
      const parentId = modifier.getParentId();

      if (!parentId) {
        this.problemJS.errorToServer(
          `Modifier missing parent id in VerticalSolvingStep.buildSize(): ${this.dto.value}`
        );

        return;
      }

      const members = idToObject.get(parentId);

      if (!members) {
        this.problemJS.errorToServer(
          `Modifier parent id ${parentId} not found in Line: ${this.dto.value}`
        );

        return;
      }

      const topLevelMembers = this.toTopLevelMembers(members);

      if (topLevelMembers.length === 0) {
        this.problemJS.errorToServer(
          `Modifier parent id ${parentId} resolved to no top-level members in Line: ${this.dto.value}`
        );

        return;
      }

      modifier.setParent(new Parent(members, topLevelMembers), this.line);

      if (modifier.viewportH > maxModifierH) {
        maxModifierH = modifier.viewportH;
      }
    });

    let maxNegativeX = 0;

    this.modifiers.forEach(modifier => {
      if (modifier.viewportX < maxNegativeX) {
        maxNegativeX = modifier.viewportX;
      }
    });

    const slideRight = Math.abs(maxNegativeX);

    this.modifiers.forEach(modifier => {
      modifier.viewportX += slideRight;
    });

    this.line.viewportX += slideRight;

    this.viewportH = this.line.viewportH + maxModifierH;
    this.viewportW = Math.max(
      this.line.viewportW + this.line.viewportX,
      ...this.modifiers.map(modifier => modifier.viewportW + modifier.viewportX)
    );
  }

  /**
   * Convert arbitrary nodes (possibly nested) into the top-level nodes under this.line.multi
   * that actually participate in left-to-right layout and sibling sliding.
   */
  private toTopLevelMembers(members: BasicObject[]): BasicObject[] {
    const root = this.line.multi;

    const topLevel = new Set<BasicObject>();

    const getParent = (obj: BasicObject): BasicObject | undefined =>
      obj.parent as BasicObject | undefined;

    const climbToTopLevel = (obj: BasicObject): BasicObject | undefined => {
      const seen = new Set<BasicObject>();

      let cur: BasicObject | undefined = obj;

      while (cur) {
        if (seen.has(cur)) {
          // cycle safety; shouldn't happen, but avoid infinite loops
          return undefined;
        }

        seen.add(cur);

        const p = getParent(cur);

        if (!p) return undefined;
        if (p === root) return cur;

        cur = p;
      }

      return undefined;
    };

    members.forEach(m => {
      const tl = climbToTopLevel(m);

      if (tl) topLevel.add(tl);
    });

    return [...topLevel];
  }

  private buildIdMap(): Map<number, BasicObject[]> {
    const map = new Map<number, BasicObject[]>();

    const collect = (obj: BasicObject) => {
      if (obj.objectFormat?.id !== undefined) {
        if (!map.has(obj.objectFormat.id)) {
          map.set(obj.objectFormat.id, []);
        }

        map.get(obj.objectFormat.id)!.push(obj);
      }

      obj.children.forEach(collect);
    };

    collect(this.line.multi);

    return map;
  }
}

export function getVerticalSolvingStep(
  problemJS: ProblemJSDependencies,
  dto: VerticalSolvingStepDTO
): VerticalSolvingStep {
  return new VerticalSolvingStep(problemJS, dto);
}
